import { useEffect, useRef, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { datadogRum } from '@wehunt/core-web'
import { huntReplayLocation, sharedReplayLocation } from '../../paths'
import { ReplayStatusScreen } from '../replay/ReplayStatusScreen'
import { MapLinkView } from './MapLinkView'
import { resolveShareLink, type ShareLinkDestination } from './resolveShareLink'
import { SHARE_LINK_OPENED_ACTION, shareLinkOpenedContext } from './shareLinkTracking'

export interface LinkRouteProps {
  /** The link service domains to ask, in order; supplied by the shell's runtime config. */
  shareLinkDomains: readonly string[]
  /**
   * Overridable so tests can observe the navigation: jsdom does not let a spy
   * replace `window.location.assign`.
   */
  leavePage?: (url: string) => void
  /** The shell's way onto a team's own map; see {@link MapLinkView}. */
  renderTeamLink?: (groupId: string) => ReactNode
}

/** A link to another site is a full document navigation, not a route change. */
function leavePageByFullLoad(url: string): void {
  window.location.assign(url)
}

/**
 * `/link?ntlkd=<code>`: the browser landing for a natlinkedin share link. The link
 * service sends every link opened on a computer here — it opens the app on a phone
 * — with the code in `ntlkd`.
 *
 * A replay link is resolved and left: both replay destinations replace this entry
 * in history, so Back from the replay does not land here and resolve the link a
 * second time. A map pin or line is shown here, on this page.
 */
export function LinkRoute({ shareLinkDomains, leavePage = leavePageByFullLoad, renderTeamLink }: Readonly<LinkRouteProps>) {
  const { t } = useTranslation('replay')
  const navigate = useNavigate()
  const location = useLocation()
  // The raw query string rather than the parsed `search`: the router JSON-parses
  // search values, which would turn a code that happens to look numeric into a
  // number, and the code must reach the link service exactly as it was sent.
  const code = new URLSearchParams(location.searchStr).get('ntlkd') ?? ''

  const { data, error, isFetching, refetch } = useQuery({
    queryKey: ['share-link', code, shareLinkDomains],
    enabled: code !== '',
    queryFn: () => resolveShareLink(code, shareLinkDomains),
    // Retrying is the visitor's call, through the Retry button: a link service
    // that is down does not come back faster for being asked again at once.
    retry: false,
    refetchOnWindowFocus: false,
  })

  // One report per resolved link: a re-render, or the same answer coming back from
  // the cache, is not another open.
  const reported = useRef<ShareLinkDestination | null>(null)
  useEffect(() => {
    if (data === undefined || isFetching || reported.current === data) return
    reported.current = data
    const context = shareLinkOpenedContext(data)
    if (context !== null) datadogRum.addAction(SHARE_LINK_OPENED_ACTION, { ...context })
  }, [data, isFetching])

  useEffect(() => {
    if (data === undefined || isFetching) return
    if (data.kind === 'sharedReplay') {
      void navigate({ to: sharedReplayLocation(data.shareId) as '/', replace: true })
    } else if (data.kind === 'huntReplay') {
      // `share_link` is what the replay reports as its source, so the share funnel
      // can tell a link open from a click inside the app.
      void navigate({
        to: huntReplayLocation(data.huntId) as '/',
        search: { source: 'share_link' },
        replace: true,
      })
    } else if (data.kind === 'elsewhere') {
      leavePage(data.url)
    }
  }, [data, isFetching, navigate, leavePage])

  const close = () => void navigate({ to: '/' })

  if (code === '' || (!isFetching && data?.kind === 'invalid')) {
    return (
      <ReplayStatusScreen
        title={t('status.linkInvalidTitle')}
        body={t('status.linkInvalidBody')}
        onClose={close}
      />
    )
  }

  if (!isFetching && (error !== null || data?.kind === 'unreachable')) {
    return (
      <ReplayStatusScreen
        title={t('status.unavailableTitle')}
        body={t('status.unavailableBody')}
        onClose={close}
        onRetry={() => void refetch()}
      />
    )
  }

  if (!isFetching && data?.kind === 'mapPin') {
    return <MapLinkView object={{ kind: 'pin', pin: data.pin }} renderTeamLink={renderTeamLink} />
  }
  if (!isFetching && data?.kind === 'mapLine') {
    return <MapLinkView object={{ kind: 'line', line: data.line }} renderTeamLink={renderTeamLink} />
  }

  // Resolving, and also the moment between resolving and the navigation landing.
  return <ReplayStatusScreen title={t('link.opening')} onClose={close} />
}
